import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import { Receipt, Plus, Trash2, ArrowLeft, Save, FileText, Mail, Calculator, Pencil, Search, X, ChevronDown, Copy, GripVertical, Type, Wrench, CircleDashed, Eye } from '../icons/wp-icons';
import Layout from '../components/Layout/Layout';
import KatalogAutocomplete from '../components/Katalog/KatalogAutocomplete';
import { rechnungApi } from '../api/rechnungApi';
import { getAllKunden, getKundeById } from '../api/kundenApi';
import { kvaApi } from '../api/kvaApi';
import { textbausteinApi } from '../api/textbausteinApi';
import DokumentVorschau from '../components/Dokumente/DokumentVorschau';

function RechnungErstellen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id: editId } = useParams(); // Optional ID for edit mode
  const isEditMode = !!editId;
  const [searchParams] = useSearchParams();
  const fromKva = location.state?.fromKva;
  const fromDuplicate = location.state?.fromDuplicate;
  const preselectedKundeId = searchParams.get('kundeId') || (fromKva?.kundeId || fromDuplicate?.kundeId || '');
  const preselectedAnfrageId = searchParams.get('anfrageId') || (fromKva?.anfrageId || fromDuplicate?.anfrageId || '');

  const [kunden, setKunden] = useState([]);
  const [kundenAnfragen, setKundenAnfragen] = useState([]);
  const [kundenKvas, setKundenKvas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAnfragen, setLoadingAnfragen] = useState(false);
  const [loadingKvas, setLoadingKvas] = useState(false);
  const [saving, setSaving] = useState(false);

  // Edit mode state
  const [rechnungNummer, setRechnungNummer] = useState('');

  // Formular-State
  const [kundeId, setKundeId] = useState(preselectedKundeId || '');
  const [anfrageId, setAnfrageId] = useState(preselectedAnfrageId || '');
  const [kvaId, setKvaId] = useState('');
  const [leistungsdatum, setLeistungsdatum] = useState(
    new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  );
  const [zahlungsziel, setZahlungsziel] = useState(14);
  const [einleitung, setEinleitung] = useState('Vielen Dank für Ihren Auftrag. Wir berechnen Ihnen wie folgt:');
  const [schlusstext, setSchlusstext] = useState('Bitte überweisen Sie den Betrag innerhalb der Zahlungsfrist auf unser Konto. Vielen Dank!');
  const [direktVersenden, setDirektVersenden] = useState(true);
  const [betreff, setBetreff] = useState('');
  const [kundenReferenz, setKundenReferenz] = useState('');
  const [gesamtRabattProzent, setGesamtRabattProzent] = useState(0);
  const [skontoProzent, setSkontoProzent] = useState('');
  const [skontoTage, setSkontoTage] = useState('');
  const [textbausteine, setTextbausteine] = useState([]);
  const [zahlungsbedingungen, setZahlungsbedingungen] = useState('');
  const [zuschlagBetrag, setZuschlagBetrag] = useState('');
  const [zuschlagBeschreibung, setZuschlagBeschreibung] = useState('');
  const [showVorschauModal, setShowVorschauModal] = useState(false);

  // Positionen
  const [positionen, setPositionen] = useState([
    { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'NORMAL', rabattProzent: 0 }
  ]);

  // Kundensuche State
  const [kundenSuche, setKundenSuche] = useState('');
  const [showKundenDropdown, setShowKundenDropdown] = useState(false);
  const [selectedKunde, setSelectedKunde] = useState(null);
  const kundenDropdownRef = useRef(null);

  useEffect(() => {
    loadKunden();
    if (isEditMode) {
      loadExistingRechnung();
    } else if (fromKva) {
      // KVA → Rechnung: Daten übernehmen
      setKundeId(fromKva.kundeId);
      setAnfrageId(fromKva.anfrageId || '');
      setKvaId(fromKva.kvaId || '');
      if (fromKva.betreff) setBetreff(fromKva.betreff);
      if (fromKva.kundenReferenz) setKundenReferenz(fromKva.kundenReferenz);
      if (fromKva.einleitung) setEinleitung(fromKva.einleitung);
      if (fromKva.schlusstext) setSchlusstext(fromKva.schlusstext);
      if (fromKva.gesamtRabattProzent) setGesamtRabattProzent(fromKva.gesamtRabattProzent);
      if (fromKva.zahlungsbedingungen) setZahlungsbedingungen(fromKva.zahlungsbedingungen);
      if (fromKva.zuschlagBetrag) setZuschlagBetrag(fromKva.zuschlagBetrag / 100);
      if (fromKva.zuschlagBeschreibung) setZuschlagBeschreibung(fromKva.zuschlagBeschreibung);
      if (fromKva.positionen?.length > 0) {
        setPositionen(fromKva.positionen.map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis / 100, // Cent → Euro
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: pos.rabattProzent || 0,
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({
                ...s,
                einzelpreis: s.einzelpreis / 100,
                gesamt: s.gesamt / 100
              }))
            : []
        })));
      }
    } else if (fromDuplicate) {
      // Rechnung duplizieren: Daten übernehmen (Preise bereits in Euro)
      setKundeId(fromDuplicate.kundeId);
      setAnfrageId(fromDuplicate.anfrageId || '');
      setKvaId(fromDuplicate.kvaId || '');
      if (fromDuplicate.betreff) setBetreff(fromDuplicate.betreff);
      if (fromDuplicate.kundenReferenz) setKundenReferenz(fromDuplicate.kundenReferenz);
      if (fromDuplicate.einleitung) setEinleitung(fromDuplicate.einleitung);
      if (fromDuplicate.schlusstext) setSchlusstext(fromDuplicate.schlusstext);
      if (fromDuplicate.gesamtRabattProzent) setGesamtRabattProzent(fromDuplicate.gesamtRabattProzent);
      if (fromDuplicate.leistungsdatum) setLeistungsdatum(fromDuplicate.leistungsdatum);
      if (fromDuplicate.skontoProzent) setSkontoProzent(fromDuplicate.skontoProzent);
      if (fromDuplicate.skontoTage) setSkontoTage(fromDuplicate.skontoTage);
      if (fromDuplicate.zahlungsbedingungen) setZahlungsbedingungen(fromDuplicate.zahlungsbedingungen);
      if (fromDuplicate.zuschlagBetrag) setZuschlagBetrag(fromDuplicate.zuschlagBetrag / 100);
      if (fromDuplicate.zuschlagBeschreibung) setZuschlagBeschreibung(fromDuplicate.zuschlagBeschreibung);
      if (fromDuplicate.positionen?.length > 0) {
        setPositionen(fromDuplicate.positionen.map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis, // Already in Euro from handleDuplicate
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: pos.rabattProzent || 0,
          berechnungsSchritte: pos.berechnungsSchritte || []
        })));
      }
    }
  }, [editId]);

  useEffect(() => {
    textbausteinApi.getAll().then(setTextbausteine).catch(console.error);
  }, []);

  const loadExistingRechnung = async () => {
    try {
      const rechnung = await rechnungApi.getRechnung(editId);
      setRechnungNummer(rechnung.rechnungsnummer);
      setKundeId(rechnung.kundeId);
      // Kunde direkt aus der Rechnung übernehmen statt ihn in der getAllKunden-Liste
      // zu suchen (die z.B. Ansprechpartner ausblendet) – sonst bleibt selectedKunde
      // null und die Vorschau sendet kundeId=null → "Kunde auswählen..."-Platzhalter.
      if (rechnung.kunde) setSelectedKunde(rechnung.kunde);
      setAnfrageId(rechnung.anfrageId || '');
      setKvaId(rechnung.kvaId || '');
      setLeistungsdatum(rechnung.leistungsdatum || '');
      setEinleitung(rechnung.einleitung || '');
      setSchlusstext(rechnung.schlusstext || '');
      setDirektVersenden(false); // Default to false in edit mode

      // Calculate zahlungsziel from datum to faelligDatum
      if (rechnung.datum && rechnung.faelligDatum) {
        const datum = new Date(rechnung.datum);
        const faelligDatum = new Date(rechnung.faelligDatum);
        const diffTime = Math.abs(faelligDatum - datum);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setZahlungsziel(diffDays);
      }

      if (rechnung.betreff) setBetreff(rechnung.betreff);
      if (rechnung.kundenReferenz) setKundenReferenz(rechnung.kundenReferenz);
      if (rechnung.gesamtRabattProzent) setGesamtRabattProzent(rechnung.gesamtRabattProzent);
      if (rechnung.skontoProzent) setSkontoProzent(rechnung.skontoProzent);
      if (rechnung.skontoTage) setSkontoTage(rechnung.skontoTage);
      if (rechnung.zahlungsbedingungen) setZahlungsbedingungen(rechnung.zahlungsbedingungen);
      if (rechnung.zuschlagBetrag) setZuschlagBetrag(rechnung.zuschlagBetrag / 100);
      if (rechnung.zuschlagBeschreibung) setZuschlagBeschreibung(rechnung.zuschlagBeschreibung);

      // Load positionen - convert cent prices back to euros
      if (rechnung.positionen && rechnung.positionen.length > 0) {
        const loadedPositionen = rechnung.positionen.map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis / 100, // Convert cents to euros
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: pos.rabattProzent || 0,
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({
                ...s,
                einzelpreis: s.einzelpreis / 100,
                gesamt: s.gesamt / 100
              }))
            : []
        }));
        setPositionen(loadedPositionen);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Rechnung:', error);
      alert('Rechnung konnte nicht geladen werden');
      navigate('/rechnungen');
    }
  };

  // Anfragen und KVAs laden wenn Kunde ausgewählt wird
  useEffect(() => {
    if (kundeId) {
      loadKundenAnfragen(kundeId);
      loadKundenKvas(kundeId);
    } else {
      setKundenAnfragen([]);
      setKundenKvas([]);
      setAnfrageId('');
      setKvaId('');
    }
  }, [kundeId]);

  // Click-Outside Handler für Kundensuche
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (kundenDropdownRef.current && !kundenDropdownRef.current.contains(event.target)) {
        setShowKundenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Setze selectedKunde wenn Kunden geladen und kundeId vorhanden
  useEffect(() => {
    if (kundeId && kunden.length > 0 && !selectedKunde) {
      const kunde = kunden.find(k => k.id === kundeId);
      if (kunde) {
        setSelectedKunde(kunde);
      }
    }
  }, [kunden, kundeId]);

  const loadKunden = async () => {
    try {
      const data = await getAllKunden();
      setKunden(data);

      // Wenn Kunde vorausgewählt, lade dessen Anfragen
      if (preselectedKundeId) {
        await loadKundenAnfragen(preselectedKundeId);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Kunden:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadKundenAnfragen = async (selectedKundeId) => {
    try {
      setLoadingAnfragen(true);
      const kundeData = await getKundeById(selectedKundeId);
      setKundenAnfragen(kundeData.anfragen || []);
    } catch (error) {
      console.error('Fehler beim Laden der Anfragen:', error);
      setKundenAnfragen([]);
    } finally {
      setLoadingAnfragen(false);
    }
  };

  const loadKundenKvas = async (selectedKundeId) => {
    try {
      setLoadingKvas(true);
      const kvas = await kvaApi.getKvas({ kundeId: selectedKundeId });
      setKundenKvas(kvas || []);
    } catch (error) {
      console.error('Fehler beim Laden der Kostenvoranschläge:', error);
      setKundenKvas([]);
    } finally {
      setLoadingKvas(false);
    }
  };

  const handleKundeChange = (newKundeId) => {
    setKundeId(newKundeId);
    setAnfrageId(''); // Anfrage zurücksetzen bei Kundenwechsel
    setKvaId(''); // KVA zurücksetzen bei Kundenwechsel
  };

  // Gefilterte Kundenliste basierend auf Suche
  const gefilterteKunden = kunden.filter(kunde => {
    const suchText = kundenSuche.toLowerCase();
    return (
      kunde.name.toLowerCase().includes(suchText) ||
      kunde.email?.toLowerCase().includes(suchText) ||
      kunde.telefon?.toLowerCase().includes(suchText)
    );
  });

  // Kunde auswählen
  const handleKundeSelect = (kunde) => {
    setSelectedKunde(kunde);
    setKundeId(kunde.id);
    setKundenSuche('');
    setShowKundenDropdown(false);
  };

  // Kundenauswahl entfernen
  const handleKundeRemove = () => {
    setSelectedKunde(null);
    setKundeId('');
    setKundenSuche('');
    setAnfrageId('');
    setKvaId('');
  };

  // KVA-Positionen übernehmen wenn KVA ausgewählt wird
  const handleKvaChange = async (selectedKvaId) => {
    setKvaId(selectedKvaId);

    if (!selectedKvaId) return;

    try {
      const kva = await kvaApi.getKva(selectedKvaId);

      if (kva.positionen && kva.positionen.length > 0) {
        // Positionen vom KVA übernehmen (Preise von Cent zu Euro konvertieren)
        const kvaPositionen = kva.positionen.map(pos => ({
          beschreibung: pos.beschreibung,
          menge: pos.menge,
          einheit: pos.einheit,
          einzelpreis: pos.einzelpreis / 100, // Cent zu Euro
          ustSatz: pos.ustSatz,
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: pos.rabattProzent || 0,
          berechnungsSchritte: pos.berechnungsSchritte
            ? pos.berechnungsSchritte.map(s => ({
                ...s,
                einzelpreis: s.einzelpreis / 100,
                gesamt: s.gesamt / 100
              }))
            : []
        }));
        setPositionen(kvaPositionen);

        // Falls KVA eine Anfrage hat, diese auch auswählen
        if (kva.anfrageId) {
          setAnfrageId(kva.anfrageId);
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden des KVA:', error);
    }
  };

  const addPosition = () => {
    setPositionen([
      ...positionen,
      { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'NORMAL', rabattProzent: 0 }
    ]);
  };

  const removePosition = (index) => {
    if (positionen.length === 1) {
      alert('Mindestens eine Position erforderlich');
      return;
    }
    setPositionen(positionen.filter((_, i) => i !== index));
  };

  const duplicatePosition = (index) => {
    const pos = { ...positionen[index] };
    const updated = [...positionen];
    updated.splice(index + 1, 0, pos);
    setPositionen(updated);
  };

  const movePosition = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= positionen.length) return;
    const updated = [...positionen];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setPositionen(updated);
  };

  const addTextPosition = () => {
    setPositionen([
      ...positionen,
      { beschreibung: '', menge: 0, einheit: '', einzelpreis: 0, ustSatz: 0, istLohn: false, positionsTyp: 'TEXT' }
    ]);
  };

  const addOptionalPosition = () => {
    setPositionen([
      ...positionen,
      { beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', ustSatz: 19, istLohn: false, positionsTyp: 'OPTIONAL', rabattProzent: 0 }
    ]);
  };

  const updatePosition = (index, field, value) => {
    const updated = [...positionen];
    updated[index][field] = value;
    setPositionen(updated);
  };

  // Berechnungsschritte helpers
  const toggleBerechnungsSchritte = (posIndex) => {
    const updated = [...positionen];
    const pos = updated[posIndex];
    if (pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0) {
      pos.berechnungsSchritte = [];
    } else {
      pos.berechnungsSchritte = [{ beschreibung: '', menge: 1, einheit: pos.einheit || 'Stk', einzelpreis: '', gesamt: 0 }];
    }
    setPositionen(updated);
  };

  const addBerechnungsSchritt = (posIndex) => {
    const updated = [...positionen];
    if (!updated[posIndex].berechnungsSchritte) updated[posIndex].berechnungsSchritte = [];
    updated[posIndex].berechnungsSchritte.push({ beschreibung: '', menge: 1, einheit: 'Stk', einzelpreis: '', gesamt: 0 });
    setPositionen(updated);
  };

  const removeBerechnungsSchritt = (posIndex, schrittIndex) => {
    const updated = [...positionen];
    updated[posIndex].berechnungsSchritte.splice(schrittIndex, 1);
    setPositionen(updated);
  };

  const updateBerechnungsSchritt = (posIndex, schrittIndex, field, value) => {
    const updated = [...positionen];
    const schritt = updated[posIndex].berechnungsSchritte[schrittIndex];
    schritt[field] = value;
    const m = parseFloat(schritt.menge) || 0;
    const p = parseFloat(schritt.einzelpreis) || 0;
    schritt.gesamt = m * p;
    setPositionen(updated);
  };

  // Berechnung
  const calculatePositionGesamt = (pos) => {
    let gesamt;
    if (pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0) {
      gesamt = pos.berechnungsSchritte.reduce((sum, s) => {
        const m = parseFloat(s.menge) || 0;
        const p = parseFloat(s.einzelpreis) || 0;
        return sum + m * p;
      }, 0);
    } else {
      const preis = parseFloat(pos.einzelpreis) || 0;
      const menge = parseFloat(pos.menge) || 0;
      gesamt = preis * menge;
    }
    const rabatt = parseFloat(pos.rabattProzent) || 0;
    if (rabatt > 0) {
      gesamt = gesamt * (1 - rabatt / 100);
    }
    return gesamt;
  };

  const calculateNetto = () => {
    const netto = positionen
      .filter(p => p.positionsTyp !== 'TEXT' && p.positionsTyp !== 'OPTIONAL')
      .reduce((sum, pos) => sum + calculatePositionGesamt(pos), 0);
    const nachRabatt = netto * (1 - (parseFloat(gesamtRabattProzent) || 0) / 100);
    return nachRabatt + (parseFloat(zuschlagBetrag) || 0);
  };

  const calculateUst = () => {
    const rabattFaktor = 1 - (parseFloat(gesamtRabattProzent) || 0) / 100;
    const posUst = positionen
      .filter(p => p.positionsTyp !== 'TEXT' && p.positionsTyp !== 'OPTIONAL')
      .reduce((sum, pos) => {
        const gesamt = calculatePositionGesamt(pos) * rabattFaktor;
        return sum + (gesamt * pos.ustSatz / 100);
      }, 0);
    const zuschlagUst = (parseFloat(zuschlagBetrag) || 0) * 0.19;
    return posUst + zuschlagUst;
  };

  const calculateBrutto = () => {
    return calculateNetto() + calculateUst();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validierung
    if (!kundeId) {
      alert('Bitte wählen Sie einen Kunden aus');
      return;
    }

    const gueltigePositionen = positionen.filter(
      p => p.beschreibung.trim() && (p.positionsTyp === 'TEXT' || parseFloat(p.einzelpreis) > 0)
    );

    if (gueltigePositionen.length === 0) {
      alert('Bitte fügen Sie mindestens eine gültige Position hinzu');
      return;
    }

    try {
      setSaving(true);

      // Positionen für API vorbereiten (Preise in Cent!)
      const positionenData = gueltigePositionen.map(pos => {
        const posData = {
          beschreibung: pos.beschreibung,
          menge: parseFloat(pos.menge) || 1,
          einheit: pos.einheit,
          einzelpreis: Math.round(parseFloat(pos.einzelpreis) * 100), // Euro zu Cent!
          ustSatz: parseFloat(pos.ustSatz),
          istLohn: pos.istLohn || false,
          positionsTyp: pos.positionsTyp || 'NORMAL',
          rabattProzent: parseFloat(pos.rabattProzent) || 0
        };
        if (pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0) {
          posData.berechnungsSchritte = pos.berechnungsSchritte
            .filter(s => s.beschreibung.trim())
            .map(s => ({
              beschreibung: s.beschreibung,
              menge: parseFloat(s.menge) || 1,
              einheit: s.einheit || 'Stk',
              einzelpreis: Math.round(parseFloat(s.einzelpreis) * 100),
              gesamt: Math.round((parseFloat(s.menge) || 1) * (parseFloat(s.einzelpreis) || 0) * 100)
            }));
        }
        return posData;
      });

      let result;

      if (isEditMode) {
        // Update existing Rechnung
        const updateData = {
          leistungsdatum: leistungsdatum || null,
          einleitung: einleitung || null,
          schlusstext: schlusstext || null,
          betreff,
          kundenReferenz,
          gesamtRabattProzent: parseFloat(gesamtRabattProzent) || 0,
          skontoProzent: skontoProzent || null,
          skontoTage: skontoTage || null,
          zahlungsbedingungen: zahlungsbedingungen || null,
          zuschlagBetrag: Math.round((parseFloat(zuschlagBetrag) || 0) * 100),
          zuschlagBeschreibung: zuschlagBeschreibung || null,
          positionen: positionenData
        };

        result = await rechnungApi.updateRechnung(editId, updateData);

        // Falls Direktversand aktiviert, sende E-Mail
        if (direktVersenden) {
          try {
            const sendResult = await rechnungApi.sendRechnung(editId);
            alert(`Rechnung ${result.rechnung.rechnungsnummer} wurde aktualisiert und an ${sendResult.rechnung?.versendetAn || 'den Kunden'} versendet!`);
          } catch (sendError) {
            console.error('Fehler beim Versenden:', sendError);
            alert(`Rechnung ${result.rechnung.rechnungsnummer} wurde aktualisiert, aber konnte nicht versendet werden: ${sendError.response?.data?.error || sendError.message}`);
          }
        } else {
          alert(`Rechnung ${result.rechnung.rechnungsnummer} wurde aktualisiert!`);
        }
      } else {
        // Create new Rechnung
        const data = {
          kundeId,
          anfrageId: anfrageId || null,
          kvaId: kvaId || null,
          leistungsdatum: leistungsdatum || null,
          zahlungsziel: parseInt(zahlungsziel),
          einleitung: einleitung || null,
          schlusstext: schlusstext || null,
          betreff,
          kundenReferenz,
          gesamtRabattProzent: parseFloat(gesamtRabattProzent) || 0,
          skontoProzent: skontoProzent || null,
          skontoTage: skontoTage || null,
          zahlungsbedingungen: zahlungsbedingungen || null,
          zuschlagBetrag: Math.round((parseFloat(zuschlagBetrag) || 0) * 100),
          zuschlagBeschreibung: zuschlagBeschreibung || null,
          positionen: positionenData,
          direktVersenden
        };

        result = await rechnungApi.createRechnung(data);

        let message = `Rechnung ${result.rechnung.rechnungsnummer} wurde erstellt!`;
        if (result.emailSent) {
          message += `\n\nE-Mail wurde erfolgreich an ${result.rechnung.versendetAn} gesendet.`;
        } else if (result.emailError) {
          message += `\n\nHinweis: E-Mail konnte nicht gesendet werden: ${result.emailError}`;
        }

        alert(message);
      }

      navigate('/rechnungen');

    } catch (error) {
      console.error(`Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'}:`, error);
      alert(`Fehler beim ${isEditMode ? 'Aktualisieren' : 'Erstellen'} der Rechnung: ` + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/rechnungen')}
            className="p-2 hover:bg-gray-100 rounded-button transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-serif font-bold flex items-center gap-3">
              {isEditMode ? <Pencil className="w-8 h-8 text-primary" /> : <Receipt className="w-8 h-8 text-primary" />}
              {isEditMode ? `Rechnung ${rechnungNummer} bearbeiten` : 'Neue Rechnung'}
            </h1>
            <p className="text-text-muted mt-1">
              {isEditMode ? 'Bearbeiten Sie die Rechnung' : 'Erstellen Sie eine neue Rechnung'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex gap-6">
          <div className="flex-1 min-w-0 space-y-6">
              {/* Kunde auswählen */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <h2 className="text-lg font-semibold mb-4">Kunde</h2>

                <div className="relative" ref={kundenDropdownRef}>
                  {/* Ausgewählter Kunde oder Suchfeld */}
                  {selectedKunde ? (
                    <div className="flex items-center justify-between w-full px-4 py-3 border border-border rounded-button bg-gray-50">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-lg block truncate">{selectedKunde.name}</span>
                        {selectedKunde.email && (
                          <span className="text-text-muted text-sm block truncate">{selectedKunde.email}</span>
                        )}
                      </div>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={handleKundeRemove}
                          className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={kundenSuche}
                        onChange={(e) => {
                          setKundenSuche(e.target.value);
                          setShowKundenDropdown(true);
                        }}
                        onFocus={() => setShowKundenDropdown(true)}
                        placeholder="Kunde suchen..."
                        className="w-full pl-10 pr-10 py-3 border border-border rounded-button focus:outline-none focus:ring-2 focus:ring-primary text-lg"
                        disabled={isEditMode}
                      />
                      <button
                        type="button"
                        onClick={() => setShowKundenDropdown(!showKundenDropdown)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        disabled={isEditMode}
                      >
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showKundenDropdown ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  )}

                  {/* Dropdown Liste */}
                  {showKundenDropdown && !selectedKunde && !isEditMode && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {loading ? (
                        <div className="px-4 py-3 text-text-muted text-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto"></div>
                        </div>
                      ) : gefilterteKunden.length === 0 ? (
                        <div className="px-4 py-3 text-text-muted text-center">
                          {kundenSuche ? 'Kein Kunde gefunden' : 'Keine Kunden vorhanden'}
                        </div>
                      ) : (
                        gefilterteKunden.map(kunde => (
                          <button
                            key={kunde.id}
                            type="button"
                            onClick={() => handleKundeSelect(kunde)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                          >
                            <div className="font-medium">{kunde.name}</div>
                            <div className="text-sm text-text-muted flex gap-3">
                              {kunde.email && <span>{kunde.email}</span>}
                              {kunde.telefon && <span>{kunde.telefon}</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {isEditMode && (
                  <p className="text-xs text-text-muted mt-1">
                    Kunde kann bei bestehenden Rechnungen nicht geändert werden
                  </p>
                )}

                {/* Anfrage-Auswahl (optional) */}
                {kundeId && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-text-muted mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Zugehörige Anfrage (optional)
                    </label>
                    {loadingAnfragen ? (
                      <div className="text-sm text-text-muted py-2">Lade Anfragen...</div>
                    ) : kundenAnfragen.length === 0 ? (
                      <div className="text-sm text-text-muted py-2 italic">
                        Keine Anfragen für diesen Kunden vorhanden
                      </div>
                    ) : (
                      <select
                        value={anfrageId}
                        onChange={(e) => setAnfrageId(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-button bg-surface"
                      >
                        <option value="">Keine Anfrage auswählen</option>
                        {kundenAnfragen.map(anfrage => (
                          <option key={anfrage.id} value={anfrage.id}>
                            {new Date(anfrage.datum).toLocaleDateString('de-DE')} - {anfrage.betreff}
                            {anfrage.kategorie && ` (${anfrage.kategorie})`}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-xs text-text-muted mt-1">
                      Die Rechnung wird mit der gewählten Anfrage verknüpft
                    </p>
                  </div>
                )}

                {/* KVA-Auswahl (optional) */}
                {kundeId && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-text-muted mb-2 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Zugehöriger Kostenvoranschlag (optional)
                    </label>
                    {loadingKvas ? (
                      <div className="text-sm text-text-muted py-2">Lade Kostenvoranschläge...</div>
                    ) : kundenKvas.length === 0 ? (
                      <div className="text-sm text-text-muted py-2 italic">
                        Keine Kostenvoranschläge für diesen Kunden vorhanden
                      </div>
                    ) : (
                      <select
                        value={kvaId}
                        onChange={(e) => handleKvaChange(e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-button bg-surface"
                      >
                        <option value="">Keinen KVA auswählen</option>
                        {kundenKvas.map(kva => (
                          <option key={kva.id} value={kva.id}>
                            {kva.nummer} - {new Date(kva.datum).toLocaleDateString('de-DE')} - {(kva.bruttogesamt / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-xs text-text-muted mt-1">
                      Positionen werden automatisch aus dem KVA übernommen
                    </p>
                  </div>
                )}
              </div>

              {/* Dokumentkopf & Details */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <h2 className="text-lg font-semibold mb-4">Dokumentkopf & Details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Betreff / Titel</label>
                      <input type="text" value={betreff} onChange={(e) => setBetreff(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="z.B. Rechnung Fenstermontage" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Ihre Zeichen / Auftragsnr.</label>
                      <input type="text" value={kundenReferenz} onChange={(e) => setKundenReferenz(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="Referenznummer des Kunden (optional)" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Leistungsdatum</label>
                      <input type="text" value={leistungsdatum} onChange={(e) => setLeistungsdatum(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="z.B. 15.11.2025 oder Nov 2025" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Zahlungsziel</label>
                      <select value={zahlungsziel} onChange={(e) => setZahlungsziel(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button">
                        <option value={7}>7 Tage</option>
                        <option value={14}>14 Tage</option>
                        <option value={21}>21 Tage</option>
                        <option value={30}>30 Tage</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">Skonto (%)</label>
                        <input type="number" step="0.5" min="0" max="10" value={skontoProzent} onChange={(e) => setSkontoProzent(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="z.B. 2" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-muted mb-1">Skonto-Tage</label>
                        <input type="number" min="1" max="90" value={skontoTage} onChange={(e) => setSkontoTage(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button" placeholder="z.B. 10" />
                      </div>
                    </div>
                    {skontoProzent && skontoTage && (
                      <p className="text-xs text-text-muted">{skontoProzent}% Skonto bei Zahlung innerhalb von {skontoTage} Tagen</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Positionen */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-semibold">Positionen</h2>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={addTextPosition}
                      className="flex items-center gap-1 text-text-muted hover:text-primary hover:underline text-sm"
                    >
                      <Type className="w-4 h-4" />
                      Textposition
                    </button>
                    <button
                      type="button"
                      onClick={addOptionalPosition}
                      className="flex items-center gap-1 text-amber-600 hover:underline text-sm"
                    >
                      <CircleDashed className="w-4 h-4" />
                      Optionale Pos.
                    </button>
                    <button
                      type="button"
                      onClick={addPosition}
                      className="flex items-center gap-1 text-primary hover:underline text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Position hinzufügen
                    </button>
                  </div>
                </div>

                {/* Katalog-Schnellauswahl */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text-muted mb-2">
                    Schnellauswahl aus Katalog
                  </label>
                  <KatalogAutocomplete
                    onSelect={(katalogPosition) => {
                      setPositionen([
                        ...positionen,
                        {
                          beschreibung: katalogPosition.bezeichnung,
                          menge: 1,
                          einheit: katalogPosition.einheit,
                          einzelpreis: katalogPosition.verkaufspreis,
                          ustSatz: katalogPosition.mwstSatz,
                          istLohn: katalogPosition.istLohn || false,
                          positionsTyp: 'NORMAL'
                        }
                      ]);
                    }}
                  />
                </div>

                <div className="space-y-4">
                  {positionen.map((pos, index) => (
                    <div key={index} className={`border rounded-button p-4 ${pos.positionsTyp === 'TEXT' ? 'border-dashed border-gray-300 bg-gray-50/50' : pos.positionsTyp === 'OPTIONAL' ? 'border-dashed border-amber-300 bg-amber-50/30' : 'border-border'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <button type="button" onClick={() => movePosition(index, -1)}
                              className="p-0.5 text-text-muted hover:text-primary transition disabled:opacity-30"
                              disabled={index === 0}>
                              <GripVertical className="w-3 h-3 rotate-90" style={{ transform: 'scaleY(-1)' }} />
                            </button>
                            <button type="button" onClick={() => movePosition(index, 1)}
                              className="p-0.5 text-text-muted hover:text-primary transition disabled:opacity-30"
                              disabled={index === positionen.length - 1}>
                              <GripVertical className="w-3 h-3 rotate-90" />
                            </button>
                          </div>
                          <span className={`text-sm font-medium ${pos.positionsTyp === 'OPTIONAL' ? 'text-amber-600 flex items-center gap-1' : 'text-text-muted'}`}>
                            {pos.positionsTyp === 'OPTIONAL' && <CircleDashed className="w-4 h-4" />}
                            {pos.positionsTyp === 'TEXT' ? `Textposition ${index + 1}` : `Position ${index + 1}`}
                            {pos.positionsTyp === 'OPTIONAL' && <span className="italic">(Optional)</span>}
                          </span>
                          {pos.positionsTyp !== 'TEXT' && (
                            <label className="flex items-center gap-1.5 text-xs text-text-muted ml-2 cursor-pointer">
                              <input type="checkbox" checked={pos.istLohn || false}
                                onChange={(e) => updatePosition(index, 'istLohn', e.target.checked)}
                                className="w-3 h-3 rounded border-border text-primary focus:ring-primary" />
                              <Wrench className="w-3 h-3" />
                              Lohn
                            </label>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => duplicatePosition(index)}
                            className="p-1 text-text-muted hover:bg-gray-100 rounded transition" title="Duplizieren">
                            <Copy className="w-4 h-4" />
                          </button>
                          <button type="button" onClick={() => removePosition(index)}
                            className="p-1 text-danger hover:bg-red-50 rounded transition" title="Löschen">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {pos.positionsTyp === 'TEXT' ? (
                        /* Text-only position */
                        <div>
                          <label className="block text-xs sm:text-sm text-text-muted mb-1">Text</label>
                          <textarea
                            value={pos.beschreibung}
                            onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                            rows={2}
                            className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            placeholder="Freitext, z.B. Zwischenüberschrift oder Hinweis..."
                          />
                        </div>
                      ) : (
                        /* Normal position with all fields */
                        <div className="grid grid-cols-12 gap-2 sm:gap-3">
                          {/* Beschreibung */}
                          <div className="col-span-12">
                            <label className="block text-xs sm:text-sm text-text-muted mb-1">
                              Beschreibung
                            </label>
                            <input
                              type="text"
                              value={pos.beschreibung}
                              onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                              className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                              placeholder="z.B. Fensterscheibe Küche 80x60cm"
                            />
                          </div>

                          {/* Menge */}
                          <div className="col-span-6 sm:col-span-3">
                            <label className="block text-xs sm:text-sm text-text-muted mb-1">
                              Menge
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={pos.menge}
                              onChange={(e) => updatePosition(index, 'menge', e.target.value)}
                              className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            />
                          </div>

                          {/* Einheit */}
                          <div className="col-span-6 sm:col-span-3">
                            <label className="block text-xs sm:text-sm text-text-muted mb-1">
                              Einheit
                            </label>
                            <select
                              value={pos.einheit}
                              onChange={(e) => updatePosition(index, 'einheit', e.target.value)}
                              className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            >
                              <option value="Stk">Stk</option>
                              <option value="Std">Std</option>
                              <option value="m²">m²</option>
                              <option value="m">m</option>
                              <option value="lfm">lfm</option>
                              <option value="kg">kg</option>
                              <option value="Pauschale">Pauschale</option>
                            </select>
                          </div>

                          {/* Einzelpreis */}
                          <div className="col-span-6 sm:col-span-3">
                            <label className="block text-xs sm:text-sm text-text-muted mb-1">
                              Preis (€)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={pos.einzelpreis}
                              onChange={(e) => updatePosition(index, 'einzelpreis', e.target.value)}
                              className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                              placeholder="0.00"
                            />
                          </div>

                          {/* UST-Satz */}
                          <div className="col-span-4 sm:col-span-2">
                            <label className="block text-xs sm:text-sm text-text-muted mb-1">
                              MwSt.
                            </label>
                            <select
                              value={pos.ustSatz}
                              onChange={(e) => updatePosition(index, 'ustSatz', parseFloat(e.target.value))}
                              className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                            >
                              <option value={19}>19%</option>
                              <option value={7}>7%</option>
                              <option value={0}>0%</option>
                            </select>
                          </div>

                          {/* Rabatt % */}
                          <div className="col-span-4 sm:col-span-2">
                            <label className="block text-xs sm:text-sm text-text-muted mb-1">
                              Rabatt %
                            </label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              max="100"
                              value={pos.rabattProzent || ''}
                              onChange={(e) => updatePosition(index, 'rabattProzent', e.target.value)}
                              className="w-full px-2 sm:px-3 py-2 border border-border rounded-button text-sm sm:text-base"
                              placeholder="0"
                            />
                          </div>
                        </div>
                      )}

                      {pos.positionsTyp !== 'TEXT' && (
                        <>
                          {/* Berechnungsschritte Toggle */}
                          <div className="mt-3 border-t border-border pt-3">
                            <button
                              type="button"
                              onClick={() => toggleBerechnungsSchritte(index)}
                              className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded transition ${
                                pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0
                                  ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                  : 'text-text-muted hover:bg-gray-100'
                              }`}
                            >
                              <Calculator className="w-3.5 h-3.5" />
                              Berechnungsschritte
                              {pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0 && (
                                <span className="bg-blue-200 text-blue-800 rounded-full px-1.5 text-[10px]">
                                  {pos.berechnungsSchritte.length}
                                </span>
                              )}
                            </button>

                            {/* Berechnungsschritte Sub-Items */}
                            {pos.berechnungsSchritte && pos.berechnungsSchritte.length > 0 && (
                              <div className="mt-2 ml-4 space-y-2">
                                {pos.berechnungsSchritte.map((schritt, sIdx) => (
                                  <div key={sIdx} className="flex items-start gap-2 bg-gray-50 rounded p-2">
                                    <div className="flex-1 grid grid-cols-12 gap-1.5">
                                      <div className="col-span-12 sm:col-span-4">
                                        <input
                                          type="text"
                                          value={schritt.beschreibung}
                                          onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'beschreibung', e.target.value)}
                                          className="w-full px-2 py-1 border border-border rounded text-xs"
                                          placeholder="Beschreibung"
                                        />
                                      </div>
                                      <div className="col-span-3 sm:col-span-2">
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0.01"
                                          value={schritt.menge}
                                          onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'menge', e.target.value)}
                                          className="w-full px-2 py-1 border border-border rounded text-xs"
                                          placeholder="Menge"
                                        />
                                      </div>
                                      <div className="col-span-3 sm:col-span-2">
                                        <select
                                          value={schritt.einheit}
                                          onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'einheit', e.target.value)}
                                          className="w-full px-1 py-1 border border-border rounded text-xs"
                                        >
                                          <option value="Stk">Stk</option>
                                          <option value="Std">Std</option>
                                          <option value="m²">m²</option>
                                          <option value="m">m</option>
                                          <option value="lfm">lfm</option>
                                          <option value="kg">kg</option>
                                          <option value="Pauschale">Pausch.</option>
                                        </select>
                                      </div>
                                      <div className="col-span-3 sm:col-span-2">
                                        <input
                                          type="number"
                                          step="0.01"
                                          min="0"
                                          value={schritt.einzelpreis}
                                          onChange={(e) => updateBerechnungsSchritt(index, sIdx, 'einzelpreis', e.target.value)}
                                          className="w-full px-2 py-1 border border-border rounded text-xs"
                                          placeholder="Preis"
                                        />
                                      </div>
                                      <div className="col-span-3 sm:col-span-2 flex items-center justify-end">
                                        <span className="text-xs font-medium text-text-muted">
                                          {formatCurrency((parseFloat(schritt.menge) || 0) * (parseFloat(schritt.einzelpreis) || 0))}
                                        </span>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeBerechnungsSchritt(index, sIdx)}
                                      className="p-1 text-danger hover:bg-red-50 rounded transition shrink-0"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => addBerechnungsSchritt(index)}
                                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                                >
                                  <Plus className="w-3 h-3" />
                                  Schritt hinzufügen
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Positions-Summe */}
                          <div className="mt-3 text-right text-sm">
                            <span className="text-text-muted">Netto: </span>
                            <span className="font-medium">
                              {formatCurrency(calculatePositionGesamt(pos))}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Texte */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <h2 className="text-lg font-semibold mb-4">Texte (optional)</h2>

                {/* Textbausteine Schnellauswahl */}
                {textbausteine.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-text-muted mb-1">Textbaustein einfügen</label>
                    <select
                      className="w-full px-3 py-2 border border-border rounded-button text-sm"
                      value=""
                      onChange={(e) => {
                        const tb = textbausteine.find(t => t.id === e.target.value);
                        if (!tb) return;
                        if (tb.kategorie === 'EINLEITUNG') {
                          setEinleitung(prev => prev ? prev + '\n' + tb.inhalt : tb.inhalt);
                        } else {
                          setSchlusstext(prev => prev ? prev + '\n' + tb.inhalt : tb.inhalt);
                        }
                      }}
                    >
                      <option value="">Textbaustein wählen...</option>
                      {textbausteine.map(tb => (
                        <option key={tb.id} value={tb.id}>
                          [{tb.kategorie === 'EINLEITUNG' ? 'Einleitung' : 'Schluss'}] {tb.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">
                      Einleitungstext
                    </label>
                    <textarea
                      value={einleitung}
                      onChange={(e) => setEinleitung(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-button"
                      placeholder="Text vor den Positionen..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">
                      Schlusstext
                    </label>
                    <textarea
                      value={schlusstext}
                      onChange={(e) => setSchlusstext(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-button"
                      placeholder="Text nach den Summen..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-muted mb-1">
                      Zahlungsbedingungen
                    </label>
                    <textarea
                      value={zahlungsbedingungen}
                      onChange={(e) => setZahlungsbedingungen(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-border rounded-button"
                      placeholder="z.B. Zahlbar innerhalb 14 Tagen ohne Abzug"
                    />
                  </div>
                </div>
              </div>

              {/* Summen */}
              <div className="bg-surface rounded-card shadow-card p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="grid grid-cols-3 gap-3 flex-1">
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Zuschlag (€)</label>
                      <input type="number" step="0.01" min="0" value={zuschlagBetrag} onChange={(e) => setZuschlagBetrag(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button text-sm" placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Beschreibung</label>
                      <input type="text" value={zuschlagBeschreibung} onChange={(e) => setZuschlagBeschreibung(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button text-sm" placeholder="z.B. Anfahrt" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-text-muted mb-1">Gesamtrabatt (%)</label>
                      <input type="number" step="0.5" min="0" max="100" value={gesamtRabattProzent} onChange={(e) => setGesamtRabattProzent(e.target.value)} className="w-full px-3 py-2 border border-border rounded-button text-sm" placeholder="0" />
                    </div>
                  </div>
                  <div className="sm:w-56 shrink-0 space-y-1">
                    {positionen.some(p => p.istLohn && p.positionsTyp !== 'TEXT') && (
                      <div className="text-xs text-text-muted space-y-0.5 pb-1 border-b border-border mb-1">
                        <div className="flex justify-between"><span>Lohn</span><span>{formatCurrency(positionen.filter(p => p.istLohn && p.positionsTyp !== 'TEXT').reduce((s, p) => s + calculatePositionGesamt(p), 0))}</span></div>
                        <div className="flex justify-between"><span>Material</span><span>{formatCurrency(positionen.filter(p => !p.istLohn && p.positionsTyp !== 'TEXT').reduce((s, p) => s + calculatePositionGesamt(p), 0))}</span></div>
                      </div>
                    )}
                    {parseFloat(zuschlagBetrag) > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-text-muted">{zuschlagBeschreibung || 'Zuschlag'}</span><span>+ {formatCurrency(parseFloat(zuschlagBetrag))}</span></div>
                    )}
                    <div className="flex justify-between text-sm"><span className="text-text-muted">Netto{parseFloat(gesamtRabattProzent) > 0 ? ` (${gesamtRabattProzent}% Rabatt)` : ''}</span><span className="font-medium">{formatCurrency(calculateNetto())}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-text-muted">MwSt.</span><span>{formatCurrency(calculateUst())}</span></div>
                    <div className="flex justify-between text-base font-bold border-t border-border pt-1 mt-1"><span>Brutto</span><span className="text-primary">{formatCurrency(calculateBrutto())}</span></div>
                  </div>
                </div>
              </div>

              {/* Spacer for sticky action bar */}
              <div className="h-24 sm:h-20"></div>
          </div>

          {/* Desktop Live-Vorschau (2xl+) */}
          <div className="hidden 2xl:block w-[550px] shrink-0">
            <DokumentVorschau
              data={{
                type: 'rechnung',
                kunde: selectedKunde,
                betreff,
                kundenReferenz,
                einleitung,
                schlusstext,
                zahlungsbedingungen,
                positionen,
                gesamtRabattProzent,
                zuschlagBetrag,
                zuschlagBeschreibung,
                skontoProzent,
                skontoTage,
                leistungsdatum,
                zahlungsziel,
              }}
            />
          </div>
          </div>

          {/* Sticky Action Bar */}
          <div className="fixed inset-x-0 bottom-[80px] sm:bottom-0 z-30 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
            <div className="container mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2 sm:gap-3">
              {/* Vorschau Button (in der Bar) */}
              <button type="button" onClick={() => setShowVorschauModal(true)} className="2xl:hidden flex items-center gap-1.5 px-3 py-2 text-sm border border-border rounded-button hover:bg-gray-100 transition shrink-0">
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Vorschau</span>
              </button>
              {/* Mail Toggle */}
              {kundeId && (() => {
                const kundeEmail = kunden.find(k => k.id === kundeId)?.email;
                return (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-button border transition shrink-0 ${!kundeEmail ? 'border-red-200 bg-red-50/50' : direktVersenden ? 'bg-blue-50 border-blue-200' : 'border-border'}`}>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={direktVersenden} onChange={(e) => setDirektVersenden(e.target.checked)} disabled={!kundeEmail} className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                    </label>
                    <Mail className={`w-4 h-4 ${direktVersenden && kundeEmail ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span className="text-sm font-medium hidden md:inline">{!kundeEmail ? 'Keine E-Mail' : direktVersenden ? 'Versenden' : 'Nicht senden'}</span>
                  </div>
                );
              })()}
              <div className="flex-1" />
              <button type="button" onClick={() => navigate('/rechnungen')} className="px-3 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-button transition hidden sm:block">Abbrechen</button>
              <button type="submit" disabled={saving} className="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 text-sm sm:text-base">
                {saving ? (
                  <span>{isEditMode ? 'Speichern...' : 'Erstellen...'}</span>
                ) : (
                  <>
                    {direktVersenden ? <Mail className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    <span className="hidden sm:inline">{direktVersenden ? (isEditMode ? 'Rechnung senden' : 'Rechnung erstellen & senden') : (isEditMode ? 'Aktualisieren' : 'Erstellen')}</span>
                    <span className="sm:hidden">{direktVersenden ? 'Senden' : (isEditMode ? 'Speichern' : 'Erstellen')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Vorschau Modal (Mobile/Tablet) */}
          {showVorschauModal && (
            <DokumentVorschau
              isModal
              onClose={() => setShowVorschauModal(false)}
              data={{
                type: 'rechnung',
                kunde: selectedKunde,
                betreff,
                kundenReferenz,
                einleitung,
                schlusstext,
                zahlungsbedingungen,
                positionen,
                gesamtRabattProzent,
                zuschlagBetrag,
                zuschlagBeschreibung,
                skontoProzent,
                skontoTage,
                leistungsdatum,
                zahlungsziel,
              }}
            />
          )}
        </form>
      </div>
    </Layout>
  );
}

export default RechnungErstellen;
